import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Play, Pause, ArrowLeft, Music2 } from "lucide-react";
import { useMusicPlayer } from "../lib/musicPlayer";
import AlbumArt from "../components/AlbumArt";

export default function AlbumDetail() {
  const { artist, album } = useParams();
  const nav = useNavigate();
  const [tracks, setTracks] = useState([]);
  const { current, isPlaying, togglePlay, playAt } = useMusicPlayer();

  const artistName = decodeURIComponent(artist);
  const albumName = decodeURIComponent(album);

  useEffect(() => {
    api.get("/tracks").then(({ data }) => setTracks(data));
  }, []);

  const albumTracks = useMemo(
    () => tracks.filter((t) => t.artist === artistName && t.album === albumName)
      .sort((a, b) => (a.track_number || 0) - (b.track_number || 0)),
    [tracks, artistName, albumName]
  );
  const artUrl = albumTracks.find((t) => t.album_art_url)?.album_art_url;

  const playTrack = (t) => {
    const idx = albumTracks.findIndex((x) => x.id === t.id);
    if (current?.id === t.id) togglePlay();
    else playAt(albumTracks, idx);
  };

  if (albumTracks.length === 0) return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-[#8A8A8A]">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#050505] pt-32 pb-32" data-testid="album-detail-page">
      <div className="px-6 md:px-12 max-w-[1500px] mx-auto">
        <button onClick={() => nav(-1)} className="flex items-center gap-2 text-white/80 hover:text-white mb-8" data-testid="album-back">
          <ArrowLeft size={16} strokeWidth={1.5} /> <span className="text-xs uppercase tracking-[0.2em]">Back</span>
        </button>

        <div className="flex items-center gap-6 mb-10">
          <div className="w-32 h-32 shrink-0 bg-[#0F0F0F] overflow-hidden">
            <AlbumArt url={artUrl} />
          </div>
          <div>
            <span className="text-xs uppercase tracking-[0.3em] text-[#D9381E]">Album</span>
            <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-white mt-2">{albumName}</h1>
            <p className="text-[#8A8A8A] mt-2">{artistName} · {albumTracks.length} tracks</p>
          </div>
        </div>

        <div className="space-y-2">
          {albumTracks.map((t) => {
            const playing = current?.id === t.id;
            return (
              <button key={t.id} onClick={() => playTrack(t)}
                className={`w-full flex items-center gap-4 p-3 border transition-colors text-left group ${playing ? "border-[#D9381E] bg-[#0F0F0F]" : "border-[#222] hover:border-[#D9381E] hover:bg-[#0F0F0F]"}`}
                data-testid={`track-${t.id}`}>
                <div className="w-9 h-9 flex items-center justify-center bg-[#0F0F0F] group-hover:bg-[#D9381E] transition-colors shrink-0">
                  {playing && isPlaying ? <Pause size={14} fill="currentColor" /> : <Play size={14} fill="currentColor" />}
                </div>
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A] w-6">{t.track_number || "-"}</span>
                <h3 className="font-display text-base text-white truncate flex-1">{t.title}</h3>
                {t.duration_seconds > 0 && <span className="text-xs text-[#8A8A8A]">{Math.floor(t.duration_seconds / 60)}:{Math.floor(t.duration_seconds % 60).toString().padStart(2, "0")}</span>}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
